/**
 * Pricing Optimization Page
 * AI-powered pricing intelligence for solopreneurs
 * Analyzes market data, competitor rates, and customer behavior
 */

import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../store/authStore';
import {
  analyzeInternalPricing,
  getServicePricing,
  getCompetitorData,
  getAIPricingRecommendations,
  generatePricingReport,
  SERVICE_CATEGORIES,
  PRICING_STRATEGIES,
} from '../services/pricingIntelligenceService';
import { formatCurrency } from '../utils/formatters';
import AddServicePricingModal from '../components/AddServicePricingModal';
import AddCompetitorModal from '../components/AddCompetitorModal';
import PricingCalculator from '../components/PricingCalculator';
import './PricingOptimization.css';

const PricingOptimization = () => {
  const user = useAuthStore((state) => state.user);

  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [internalData, setInternalData] = useState(null);
  const [servicePricing, setServicePricing] = useState([]);
  const [competitorData, setCompetitorData] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [activeTab, setActiveTab] = useState('overview'); // overview, services, competitors, calculator
  const [showAddServiceModal, setShowAddServiceModal] = useState(false);
  const [showAddCompetitorModal, setShowAddCompetitorModal] = useState(false);
  const [country, setCountry] = useState('Philippines');

  useEffect(() => {
    loadData();
  }, [user]);

  const loadData = async () => {
    if (!user) return;

    try {
      setLoading(true);
      const [internal, services, competitors] = await Promise.all([
        analyzeInternalPricing(user.uid),
        getServicePricing(user.uid),
        getCompetitorData(user.uid),
      ]);

      setInternalData(internal);
      setServicePricing(services);
      setCompetitorData(competitors);
    } catch (error) {
      console.error('Error loading pricing data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyze = async () => {
    if (!user || !internalData) return;

    try {
      setAnalyzing(true);
      const newRecommendations = [];

      for (const service of servicePricing) {
        const competitorsForService = competitorData.filter(
          c => c.serviceCategory === service.category
        );

        const recommendation = await getAIPricingRecommendations({
          internalData,
          competitorData: competitorsForService,
          serviceCategory: service.category,
          country,
          targetMargin: service.targetMargin || 30,
        });

        newRecommendations.push({
          serviceName: service.serviceName,
          serviceId: service.id,
          currentRate: service.currentRate,
          ...recommendation,
        });
      }

      setRecommendations(newRecommendations);
    } catch (error) {
      console.error('Error analyzing pricing:', error);
      alert('Error analyzing pricing: ' + error.message);
    } finally {
      setAnalyzing(false);
    }
  };

  const handleGenerateReport = async () => {
    try {
      setAnalyzing(true);
      const report = await generatePricingReport(user.uid, { country });
      alert('Pricing report generated successfully!');
      console.log('Report:', report);
    } catch (error) {
      console.error('Error generating report:', error);
      alert('Error generating report: ' + error.message);
    } finally {
      setAnalyzing(false);
    }
  };


  return (
    <div className="pricing-optimization">
      <div className="page-header">
        <div>
          <h1>Pricing Optimization</h1>
          <p className="page-subtitle">
            AI-powered pricing intelligence based on your costs, market data, and competitor analysis
          </p>
        </div>
        <div className="header-actions">
          <button
            className="btn btn-secondary"
            onClick={handleGenerateReport}
            disabled={analyzing}
          >
            Generate Report
          </button>
          <button
            className="btn btn-primary"
            onClick={handleAnalyze}
            disabled={analyzing || servicePricing.length === 0}
          >
            {analyzing ? 'Analyzing...' : 'Analyze Pricing'}
          </button>
        </div>
      </div>

      {/* Country Selection */}
      <div className="country-selector">
        <label htmlFor="country">Target Market:</label>
        <select
          id="country"
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          className="input"
        >
          <option value="Philippines">Philippines</option>
          <option value="United States">United States</option>
          <option value="United Kingdom">United Kingdom</option>
          <option value="Canada">Canada</option>
          <option value="Australia">Australia</option>
          <option value="Singapore">Singapore</option>
          <option value="India">India</option>
        </select>
      </div>

      {/* Tabs */}
      <div className="tabs">
        <button
          className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          className={`tab ${activeTab === 'services' ? 'active' : ''}`}
          onClick={() => setActiveTab('services')}
        >
          My Services ({servicePricing.length})
        </button>
        <button
          className={`tab ${activeTab === 'competitors' ? 'active' : ''}`}
          onClick={() => setActiveTab('competitors')}
        >
          Competitors ({competitorData.length})
        </button>
        <button
          className={`tab ${activeTab === 'calculator' ? 'active' : ''}`}
          onClick={() => setActiveTab('calculator')}
        >
          Price Calculator
        </button>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="tab-content">
          {/* Key Metrics */}
          {internalData && (
            <div className="metrics-grid">
              <div className="metric-card">
                <div className="metric-label">Total Revenue</div>
                <div className="metric-value">{formatCurrency(internalData.summary.totalRevenue)}</div>
                <div className="metric-subtitle">From {internalData.summary.invoiceCount} invoices</div>
              </div>
              <div className="metric-card">
                <div className="metric-label">Profit Margin</div>
                <div className="metric-value">{internalData.summary.profitMargin.toFixed(1)}%</div>
                <div className="metric-subtitle">
                  Net: {formatCurrency(internalData.summary.netProfit)}
                </div>
              </div>
              <div className="metric-card">
                <div className="metric-label">Avg Invoice</div>
                <div className="metric-value">
                  {formatCurrency(internalData.summary.avgInvoiceAmount)}
                </div>
                <div className="metric-subtitle">Per project</div>
              </div>
              <div className="metric-card">
                <div className="metric-label">Total Expenses</div>
                <div className="metric-value">{formatCurrency(internalData.summary.totalExpenses)}</div>
                <div className="metric-subtitle">{internalData.summary.expenseCount} recorded</div>
              </div>
            </div>
          )}

          {/* AI Recommendations */}
          {recommendations.length > 0 && (
            <div className="recommendations-section">
              <h2>AI Pricing Recommendations</h2>
              <div className="recommendations-grid">
                {recommendations.map((rec, index) => (
                  <div key={index} className="recommendation-card">
                    <div className="rec-header">
                      <h3>{rec.serviceName}</h3>
                      <span className={`confidence-badge ${rec.confidence}`}>
                        {rec.confidence} confidence
                      </span>
                    </div>

                    <div className="pricing-comparison">
                      <div className="price-col">
                        <div className="price-label">Current Rate</div>
                        <div className="price-value">{formatCurrency(rec.currentRate)}</div>
                      </div>
                      <div className="price-arrow">→</div>
                      <div className="price-col highlight">
                        <div className="price-label">Suggested Rate</div>
                        <div className="price-value">{formatCurrency(rec.suggestedRate)}</div>
                      </div>
                    </div>

                    {rec.localizedRate && (
                      <div className="localized-rate">
                        Localized for {country}: {formatCurrency(rec.localizedRate)}
                      </div>
                    )}

                    <div className="market-position">
                      <span className={`position-badge ${rec.marketPosition}`}>
                        {rec.marketPosition} positioning
                      </span>
                    </div>

                    <div className="reasoning">
                      <strong>Analysis:</strong>
                      <ul>
                        {rec.reasoning.map((reason, idx) => (
                          <li key={idx}>{reason}</li>
                        ))}
                      </ul>
                    </div>

                    {rec.adjustmentFactors && rec.adjustmentFactors.length > 0 && (
                      <div className="adjustment-factors">
                        <strong>Key Factors:</strong>
                        {rec.adjustmentFactors.map((factor, idx) => (
                          <div key={idx} className={`factor ${factor.impact}`}>
                            <span className="factor-name">{factor.factor}</span>
                            <span className="factor-amount">
                              {factor.impact === 'increase' ? '+' : '-'}
                              {formatCurrency(Math.abs(factor.amount))}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Service Performance */}
          {internalData && internalData.serviceAnalysis && (
            <div className="service-performance">
              <h2>Service Performance Analysis</h2>
              <div className="performance-table">
                <table>
                  <thead>
                    <tr>
                      <th>Service</th>
                      <th>Times Sold</th>
                      <th>Total Revenue</th>
                      <th>Avg Rate</th>
                      <th>Rate Range</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(internalData.serviceAnalysis).map(([service, data]) => (
                      <tr key={service}>
                        <td><strong>{service}</strong></td>
                        <td>{data.count}</td>
                        <td>{formatCurrency(data.totalRevenue)}</td>
                        <td>{formatCurrency(data.avgRate)}</td>
                        <td>
                          {formatCurrency(data.minRate)} - {formatCurrency(data.maxRate)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Top Clients */}
          {internalData && internalData.clientMetrics && (
            <div className="client-insights">
              <h2>Client Insights</h2>
              <div className="client-cards">
                {Object.entries(internalData.clientMetrics)
                  .sort((a, b) => b[1].totalRevenue - a[1].totalRevenue)
                  .slice(0, 5)
                  .map(([clientId, metrics]) => (
                    <div key={clientId} className="client-card">
                      <h3>{metrics.clientName}</h3>
                      <div className="client-stat">
                        <span className="stat-label">Total Revenue:</span>
                        <span className="stat-value">{formatCurrency(metrics.totalRevenue)}</span>
                      </div>
                      <div className="client-stat">
                        <span className="stat-label">Avg Invoice:</span>
                        <span className="stat-value">{formatCurrency(metrics.avgInvoiceValue)}</span>
                      </div>
                      <div className="client-stat">
                        <span className="stat-label">Invoices:</span>
                        <span className="stat-value">{metrics.invoiceCount}</span>
                      </div>
                      {metrics.avgPaymentDays !== undefined && (
                        <div className="client-stat">
                          <span className="stat-label">Avg Payment Time:</span>
                          <span className="stat-value">{metrics.avgPaymentDays.toFixed(0)} days</span>
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Services Tab */}
      {activeTab === 'services' && (
        <div className="tab-content">
          <div className="section-header">
            <h2>My Service Pricing</h2>
            <button className="btn btn-primary" onClick={() => setShowAddServiceModal(true)}>
              + Add Service
            </button>
          </div>

          {servicePricing.length === 0 ? (
            <div className="empty-state">
              <p>No services added yet. Add your first service to start optimizing pricing.</p>
              <button className="btn btn-primary" onClick={() => setShowAddServiceModal(true)}>
                Add Your First Service
              </button>
            </div>
          ) : (
            <div className="services-grid">
              {servicePricing.map((service) => (
                <div key={service.id} className="service-card">
                  <div className="service-header">
                    <h3>{service.serviceName}</h3>
                    <span className="service-category">{service.category}</span>
                  </div>
                  <p className="service-description">{service.description}</p>
                  <div className="service-pricing">
                    <div className="price-row">
                      <span>Current Rate:</span>
                      <strong>{formatCurrency(service.currentRate)}</strong>
                    </div>
                    {service.suggestedRate && (
                      <div className="price-row suggested">
                        <span>Suggested Rate:</span>
                        <strong>{formatCurrency(service.suggestedRate)}</strong>
                      </div>
                    )}
                    <div className="price-row">
                      <span>Target Margin:</span>
                      <strong>{service.targetMargin}%</strong>
                    </div>
                    <div className="price-row">
                      <span>Strategy:</span>
                      <strong>{service.strategy}</strong>
                    </div>
                  </div>
                  {service.costs && (
                    <div className="service-costs">
                      <div className="cost-item">
                        Direct Costs: {formatCurrency(service.costs.directCosts)}
                      </div>
                      <div className="cost-item">
                        Indirect Costs: {formatCurrency(service.costs.indirectCosts)}
                      </div>
                      {service.costs.timeHours > 0 && (
                        <div className="cost-item">
                          Estimated Time: {service.costs.timeHours}h
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Competitors Tab */}
      {activeTab === 'competitors' && (
        <div className="tab-content">
          <div className="section-header">
            <h2>Competitor Analysis</h2>
            <button className="btn btn-primary" onClick={() => setShowAddCompetitorModal(true)}>
              + Add Competitor
            </button>
          </div>

          {competitorData.length === 0 ? (
            <div className="empty-state">
              <p>No competitor data yet. Add competitor pricing to benchmark your rates.</p>
              <button className="btn btn-primary" onClick={() => setShowAddCompetitorModal(true)}>
                Add Competitor Data
              </button>
            </div>
          ) : (
            <div className="competitors-table">
              <table>
                <thead>
                  <tr>
                    <th>Competitor</th>
                    <th>Service Category</th>
                    <th>Rate</th>
                    <th>Rate Type</th>
                    <th>Country</th>
                    <th>Quality</th>
                    <th>Source</th>
                  </tr>
                </thead>
                <tbody>
                  {competitorData.map((competitor) => (
                    <tr key={competitor.id}>
                      <td><strong>{competitor.competitorName}</strong></td>
                      <td>{competitor.serviceCategory}</td>
                      <td>{formatCurrency(competitor.rate)}</td>
                      <td>{competitor.rateType}</td>
                      <td>{competitor.country}</td>
                      <td>
                        <div className="quality-stars">
                          {'★'.repeat(competitor.qualityRating)}
                          {'☆'.repeat(5 - competitor.qualityRating)}
                        </div>
                      </td>
                      <td>
                        {competitor.sourceUrl ? (
                          <a href={competitor.sourceUrl} target="_blank" rel="noopener noreferrer">
                            {competitor.source}
                          </a>
                        ) : (
                          competitor.source
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Calculator Tab */}
      {activeTab === 'calculator' && (
        <div className="tab-content">
          <PricingCalculator />
        </div>
      )}

      {/* Modals */}
      {showAddServiceModal && (
        <AddServicePricingModal
          onClose={() => setShowAddServiceModal(false)}
          onSuccess={() => {
            setShowAddServiceModal(false);
            loadData();
          }}
        />
      )}

      {showAddCompetitorModal && (
        <AddCompetitorModal
          onClose={() => setShowAddCompetitorModal(false)}
          onSuccess={() => {
            setShowAddCompetitorModal(false);
            loadData();
          }}
        />
      )}
    </div>
  );
};

export default PricingOptimization;
